import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { Number3 } from '@fabric-web/3d-viewer';
import { Box } from '@mui/material';
import { ImageNumbering, DialogContentStyle, MainContentContainer } from './styles';
import { SelectedPoiContent } from '@/components/Analysis/modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent';
import {
  useGetStructureForInspectionDataLoaderQuery,
  GenerateImageInput,
  PointOfInterest,
  FileUploadStatus,
  ImageType,
} from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useDrawerWidths } from '@/hooks/useDrawerWidths';
import { useFoundViewpoints } from '@/components/Analysis/modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/hooks/useFoundViewpoints';
import { mapAttachmentType } from '@/components/Analysis/modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/utils';
import { thumbnailInputFromLookAt } from '@/components/Analysis/Viewer/Drawers/FindViewpointsTool/ViewpointThumbnail';
import { useGenerateImage } from '@/components/Analysis/modules/thumbnails';
import { useImageZoomAndDrag } from '@/hooks/useImageZoomAndDrag';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { MainImageSection } from './MainImageSection';
import { ThumbnailList } from './ThumbnailList';
import { poiState } from '@/components/Analysis/modules/pointOfInterest';
import { sortPoiAttachments, isImageFile } from '@/utils';
import { ImageWithId } from '@/types';
import { PointOfInterestCaption } from '@/components/Analysis/ReviewScreen/PointsOfInterest/PointOfInterestCaption/PointOfInterestCaption';
import { LocationTag } from '@/constants';
import { poiAttachmentsAtom } from '@/components/Analysis/modules/pointOfInterest/state';
import { useInsightsPageParams } from '@/hooks';

enum NavigationDirection {
  Next = 'next',
  Previous = 'previous',
}

export const ImagePreviewContent = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [viewpointImages, setViewpointImages] = useState<ImageWithId[]>([]);
  const [mainImageLoading, setMainImageLoading] = useState(false);
  const setInspectionMetadata = useSetAtom(state.inspectionMetadata);
  const selectedPoiId = useAtomValue(poiState.selectedPointOfInterestId);
  const allPois = useAtomValue(poiState.structurePois);
  const fallbackPoi = useAtomValue(poiState.selectedPointOfInterest) as PointOfInterest | undefined;
  const structureId = useGetStructureId();
  const setCurrentPoi = useSetAtom(poiState.selectedPointOfInterest);
  const { rightDrawerWidth } = useDrawerWidths();
  const poiAttachments = useAtomValue(poiAttachmentsAtom);
  const { getCurrentParams, setReviewImage } = useInsightsPageParams();

  const [pendingImageId, setPendingImageId] = useState<string | undefined>(
    () => getCurrentParams().reviewImage as string | undefined
  );

  const [activePoiIndex, setActivePoiIndex] = useState(() =>
    allPois.findIndex((p) => p.id === selectedPoiId)
  );

  useEffect(() => {
    const index = allPois.findIndex((p) => p.id === selectedPoiId);
    if (index !== -1) setActivePoiIndex(index);
  }, [selectedPoiId, allPois]);

  useEffect(() => {
    setPendingImageId(getCurrentParams().reviewImage as string | undefined);
    setCurrentImageIndex(0);
  }, [selectedPoiId, getCurrentParams]);

  const currentPoi = (
    activePoiIndex >= 0 ? allPois[activePoiIndex] : fallbackPoi
  ) as PointOfInterest;

  useEffect(() => {
    const poi = allPois[activePoiIndex];
    if (activePoiIndex >= 0 && poi) {
      setCurrentPoi(poi as typeof fallbackPoi);
    }
  }, [activePoiIndex, allPois, setCurrentPoi]);

  const { imageTransform, imageEventHandlers, handleZoom, resetImageState, canZoomIn, canZoomOut } =
    useImageZoomAndDrag({
      maxScale: 3,
      minScale: 1,
      scaleStep: 0.5,
      xLimitMultiplier: 200,
      yLimitMultiplier: 150,
    });

  const { data: structureData } = useGetStructureForInspectionDataLoaderQuery({
    variables: { structureId },
    fetchPolicy: 'cache-first',
    skip: !structureId,
  });

  const poiCenterPoint = useMemo(() => {
    const { x, y, z } = currentPoi?.centerPoint3d ?? {};
    const centerPoint3dExists = x !== undefined && y !== undefined && z !== undefined;
    return centerPoint3dExists ? ([x, y, z] as Number3) : undefined;
  }, [currentPoi?.centerPoint3d]);

  const { foundViewpoints } = useFoundViewpoints(poiCenterPoint);
  const { fetchCropFromSpherical } = useGenerateImage();

  const fileData = useMemo(() => {
    return (
      poiAttachments
        ?.filter(
          (file) =>
            file.uploadStatus === FileUploadStatus.Success &&
            file.imageType !== ImageType.WhiteBgPdf &&
            Boolean(isImageFile(file))
        )
        .map((file) => ({
          id: file.id,
          uuid: file.uuid,
          path: file.resourcePath,
          name: file.name || 'Attachment',
          caption: file.caption ?? undefined,
          imageType: file.imageType,
          fileName: file.fileName,
        })) || []
    );
  }, [poiAttachments]);

  useEffect(() => {
    if (structureData?.structure) {
      setInspectionMetadata(structureData.structure);
    }
  }, [structureData?.structure, setInspectionMetadata]);

  useEffect(() => {
    const fetchViewpointImages = async () => {
      if (!foundViewpoints?.length) {
        setViewpointImages([]);
        return;
      }

      const viewpointPlaceholders: ImageWithId[] = foundViewpoints.map((viewpoint) => ({
        id: viewpoint.id,
        name: viewpoint.name,
        viewpointId: viewpoint.id,
        src: '',
        isLoading: true,
        imageType: 'viewpoint',
      }));
      setViewpointImages(viewpointPlaceholders);

      const imageFetchPromises = foundViewpoints.map(async (viewpoint) => {
        const input = thumbnailInputFromLookAt(viewpoint, poiCenterPoint) as GenerateImageInput;
        if (!input) return { id: viewpoint.id, src: '', isLoading: false };

        try {
          const imageData = await fetchCropFromSpherical(input);
          const src = imageData ? `data:image/jpeg;base64,${imageData}` : '';
          return { id: viewpoint.id, src, isLoading: false };
        } catch {
          return { id: viewpoint.id, src: '', isLoading: false };
        }
      });

      const resolvedImages = await Promise.all(imageFetchPromises);

      setViewpointImages((prev) =>
        prev.map((img) => {
          const updatedViewpoints = resolvedImages.find((u) => u.id === img.id);
          return updatedViewpoints ? { ...img, ...updatedViewpoints } : img;
        })
      );
    };

    fetchViewpointImages();
  }, [foundViewpoints, poiCenterPoint, fetchCropFromSpherical]);

  const imagesWithIds = useMemo(() => {
    const imageList: ImageWithId[] = [];
    fileData.forEach((img) => {
      imageList.push({
        src: getCloudfrontUrl(img.path ?? ''),
        id: img.id!,
        uuid: img.uuid ?? undefined,
        name: img.name,
        imageType: img.imageType ?? ImageType.Other,
        fileName: img.fileName,
        caption: img.caption ?? undefined,
      });
    });

    const hasPrimary = imageList.find((image) => image.imageType === ImageType.Primary);
    if (!hasPrimary && currentPoi?.screenshotPaths?.viewpoint) {
      imageList.push({
        src: getCloudfrontUrl(currentPoi.screenshotPaths.viewpoint),
        id: 'viewpoint',
        name: currentPoi?.location?.name || 'Primary Image',
        imageType: ImageType.Primary,
        caption: undefined,
      });
    }
    const allImages = [...imageList, ...viewpointImages];
    return sortPoiAttachments(allImages, hasPrimary);
  }, [currentPoi?.screenshotPaths, currentPoi?.location?.name, fileData, viewpointImages]);

  // Resolve the pending image ID from the URL param once images are available.
  useEffect(() => {
    if (!pendingImageId || imagesWithIds.length === 0) return;
    const index = imagesWithIds.findIndex((img) => img.id === pendingImageId);
    if (index !== -1) {
      setCurrentImageIndex(index);
      setPendingImageId(undefined);
    }
  }, [pendingImageId, imagesWithIds]);

  const currentImage = imagesWithIds[currentImageIndex];

  const handleImageNavigation = useCallback(
    (direction: NavigationDirection) => () => {
      const total = imagesWithIds.length;
      const newIndex =
        direction === NavigationDirection.Previous
          ? currentImageIndex > 0
            ? currentImageIndex - 1
            : total - 1
          : currentImageIndex < total - 1
            ? currentImageIndex + 1
            : 0;

      setCurrentImageIndex(newIndex);
      resetImageState();
      const newImage = imagesWithIds[newIndex];
      if (newImage) {
        setReviewImage(newImage.id);
        if (!newImage.isLoading && newImage.imageType !== 'viewpoint') {
          setMainImageLoading(true);
        }
      }
    },
    [imagesWithIds, currentImageIndex, resetImageState, setReviewImage]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const activeEl = document.activeElement as HTMLElement | null;
      if (
        activeEl &&
        (activeEl.tagName === 'INPUT' ||
          activeEl.tagName === 'TEXTAREA' ||
          activeEl.isContentEditable)
      ) {
        return;
      }
      if (event.key === 'ArrowLeft') {
        handleImageNavigation(NavigationDirection.Previous)();
      } else if (event.key === 'ArrowRight') {
        handleImageNavigation(NavigationDirection.Next)();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleImageNavigation, imagesWithIds.length, currentImageIndex]);

  const handleThumbnailClick = (index: number) => {
    if (imagesWithIds[index].isLoading) return;

    setCurrentImageIndex(index);
    resetImageState();

    const targetImage = imagesWithIds[index];
    if (targetImage) {
      setReviewImage(targetImage.id);
      if (!targetImage.isLoading && targetImage.imageType !== 'viewpoint' && index !== currentImageIndex) {
        setMainImageLoading(true);
      }
    }
  };

  const handleImageLoad = () => {
    setMainImageLoading(false);
  };

  const handleImageError = () => {
    setMainImageLoading(false);
  };

  const selectedAttachment = useMemo(() => {
    return mapAttachmentType(currentImage);
  }, [currentImage]);

  if (!currentPoi) return null;

  return (
    <Box sx={DialogContentStyle}>
      <MainContentContainer>
        <Box sx={{ paddingBottom: '0' }}>
          <MainImageSection
            currentImage={currentImage}
            mainImageLoading={mainImageLoading}
            imageTransform={imageTransform}
            imageEventHandlers={imageEventHandlers}
            handleImageLoad={handleImageLoad}
            handleImageError={handleImageError}
            canZoomIn={canZoomIn}
            canZoomOut={canZoomOut}
            handleZoom={handleZoom}
            previousButtonId={`${LocationTag.ReviewPanelPoiModal}-previous-image-button`}
            nextButtonId={`${LocationTag.ReviewPanelPoiModal}-next-image-button`}
            onPrevious={handleImageNavigation(NavigationDirection.Previous)}
            onNext={handleImageNavigation(NavigationDirection.Next)}
            isNavigationDisabled={imagesWithIds.length <= 1}
          />
        </Box>
        <Box
          width="100%"
          display="flex"
          flexDirection="column"
          alignItems="center"
          sx={{
            marginTop: '2.4rem',
            marginBottom: '2.4rem',
            gap: '0.4rem',
          }}
        >
          {selectedAttachment && (
            <Box
              sx={{
                maxWidth: '100%',
                '& .MuiTypography-root': {
                  fontSize: '1.4rem',
                  fontWeight: 700,
                  wordBreak: 'break-word',
                  textAlign: 'center',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                },
              }}
            >
              <PointOfInterestCaption
                selectedAttachment={selectedAttachment}
                selectedPointOfInterest={currentPoi}
                location={LocationTag.ReviewPanelPoiModal}
              />
            </Box>
          )}
          <ImageNumbering>{`${currentImageIndex + 1}/${imagesWithIds.length}`}</ImageNumbering>
        </Box>
        <ThumbnailList
          pointOfInterestId={currentPoi.id}
          imagesWithIds={imagesWithIds}
          currentIndex={currentImageIndex}
          handleThumbnailClick={handleThumbnailClick}
        />
      </MainContentContainer>
      <Box
        sx={{
          width: `${rightDrawerWidth}px`,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <SelectedPoiContent
          selectedPoiId={currentPoi.id}
          closeHandler={() => setCurrentPoi(undefined)}
          showBackButton={false}
          showHeader={false}
          showReviewControls={false}
        />
      </Box>
    </Box>
  );
};
